import { toReportNumber } from './reportNumbers';

const formatHour12 = (hour) => {
  const h = Number(hour);

  if (!Number.isFinite(h) || h < 0 || h > 23) {
    return "";
  }

  const suffix = h < 12 ? "AM" : "PM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;

  return `${hour12}:00 ${suffix}`;
};

// Shared row builder ensures exports and the visible Overview use identical calculations.
export const buildOverviewReportRows = ({ ordersByDay = [], avgBumpTimeByDay = [], ordersList = [], ordersBumpMinutes = [] }) => {
const dailyMap = new Map();

    const ensureDay = (date) => {
      const day = String(date || "").slice(0, 10);

      if (!day) return null;

      if (!dailyMap.has(day)) {
        dailyMap.set(day, {
          id: day,
          date: day,
          orders: 0,
          avgBump: null,
          bumpValues: [],
          performanceStatuses: [],
          ticketsCompleted: 0,
          hourlyOrders: new Map(),
        });
      }

      return dailyMap.get(day);
    };

    // Add backend daily ticket totals.
    for (const row of ordersByDay) {
      const day = ensureDay(row?.date);

      if (!day) continue;

      day.orders = Number(row?.total_orders || 0);
    }

    // Add backend daily average bump time.
    for (const row of avgBumpTimeByDay) {
      const day = ensureDay(row?.date);

      if (!day) continue;

      const avg = toReportNumber(row?.avg_bump_time_minutes);

      if (Number.isFinite(avg)) {
        day.avgBump = avg;
      }
    }

    // Create an order ID lookup for constant-time bump-duration access.
    const bumpMap = new Map();

    for (const row of ordersBumpMinutes) {
      const orderId = row?.order_id;
      const bumpMinutes = toReportNumber(row?.bump_minutes);

      if (
        orderId == null ||
        !Number.isFinite(bumpMinutes) ||
        bumpMinutes < 0
      ) {
        continue;
      }

      bumpMap.set(String(orderId), {
        minutes: bumpMinutes,
        status: row?.performance_status || "Insufficient Data",
      });
    }

    // Process individual tickets to calculate daily median, completion, and performance values.
    for (const order of ordersList) {
      const orderId = String(order?.order_id ?? "");

      const receivedAt =
        order?.received_at ||
        order?.created_at ||
        order?.date;

      const orderDate = String(receivedAt || "").slice(0, 10);

      if (!orderDate) continue;

      const day = ensureDay(orderDate);

      if (!day) continue;

      // Count received orders by hour to identify the daily peak hour.
      if (receivedAt) {
        // Read the backend-localized hour directly so the browser timezone cannot shift it.
        const hourMatch = String(receivedAt).match(
          /^\d{4}-\d{2}-\d{2}[T ](\d{2}):/,
        );
        if (hourMatch) {
          const hour = Number(hourMatch[1]);
          if (Number.isInteger(hour) && hour >= 0 && hour <= 23) {
            day.hourlyOrders.set(
              hour,
              (day.hourlyOrders.get(hour) || 0) + 1
            );
          }
        }
      }

      // Use the backend lifecycle duration and classification as one consistent result.
      const bumpResult = bumpMap.get(orderId);
      const bumpMinutes = bumpResult?.minutes;

      if (
        Number.isFinite(bumpMinutes) &&
        bumpMinutes >= 0
      ) {
        day.bumpValues.push(bumpMinutes);
        day.performanceStatuses.push(bumpResult.status);
        day.ticketsCompleted += 1;
      }
    }

    return Array.from(dailyMap.values())
      .sort((a, b) =>
        String(a.date).localeCompare(String(b.date))
      )
      .map((day) => {
        // Sort valid ticket bump times before calculating the median.
        const bumpValues = [...day.bumpValues]
          .filter(
            (value) =>
              Number.isFinite(value) &&
              value >= 0
          )
          .sort((a, b) => a - b);

        // Calculate the middle bump duration for the day.
        let median = null;

        if (bumpValues.length) {
          const middle = Math.floor(bumpValues.length / 2);

          median =
            bumpValues.length % 2 === 1
              ? bumpValues[middle]
              : (bumpValues[middle - 1] +
                  bumpValues[middle]) /
                2;
        }

        // Use the backend-provided daily average when available.
        const avg = Number.isFinite(day.avgBump)
          ? day.avgBump
          : null;

        // NEEDS REVIEW & RESEARCH: Define whether equal peak-hour counts should prefer the earliest hour or display every tied peak.
        // Find the hour with the highest received-order count.
        let peakHour = "-";
        let peakHourOrders = 0;

        for (const [hour, count] of day.hourlyOrders) {
          if (
            count > peakHourOrders ||
            (count === peakHourOrders &&
              (!Number.isFinite(peakHour) || hour < peakHour))
          ) {
            peakHourOrders = count;
            peakHour = hour;
          }
        }

        if (Number.isFinite(peakHour)) {
          peakHour = formatHour12(peakHour);
        }

        const baseline = ordersBumpMinutes.find(
          (row) => row?.performance_baseline,
        )?.performance_baseline;
        let timePerformance = "Insufficient Data";

        // Compare the daily median to the same adaptive client baseline used for tickets.
        if (Number.isFinite(median) && baseline?.sufficient_data) {
          if (median <= Number(baseline.normal_limit_minutes)) {
            timePerformance = "Normal";
          } else if (median <= Number(baseline.review_limit_minutes)) {
            timePerformance = "Needs Review";
          } else {
            timePerformance = "Outlier";
          }
        }

        const outlierCount = day.performanceStatuses.filter(
          (status) => status === "Outlier",
        ).length;

        return {
          id: day.date,
          date: day.date,
          orders: day.orders,
          avg_bump: avg,
          median_bump: median,
          slow_tickets: outlierCount,
          peak_hour: peakHour,
          time_performance: timePerformance,
          tickets_completed: day.ticketsCompleted,
        };
      });
};
